import { Alert, Box, Button } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useSnackbar } from "notistack";
import type { PropsWithChildren } from "react";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useResetRecoilState, useSetRecoilState } from "recoil";
import { Agent, Company, PropallyAgentV1Api, AgentProfileImage } from "../../api/generated/src";
import { getApiErrorStatus, isAccountAccessBlocked } from "../../api/sentryApiErrorPolicy";
import { consumeImpersonationLoginFailed } from "../../auth/accessToken";
import { DoingOverlay } from "../../components/Organisms/DoingOverlay";
import {
  AgentState,
  CompanyState,
  LoggedInState,
  MasterAgentSearchTagsState,
  MasterInterestedPropertyTypesState,
} from "../../view_models/atoms";
import { masterDataStatusState } from "../../view_models/masterData";
import { UnreadMatchingCount } from "../../view_models/unreadMatchingCount";
import { MasterDataProvider } from "../MasterDataProvider";

const fetchAgent = (): Promise<Agent> =>
  new Promise((resolve, reject) => {
    new PropallyAgentV1Api().agentV1AgentGet((error: object, data: Agent, response: object) =>
      error ? reject(response ?? error) : resolve(data)
    );
  });

const fetchCompany = (): Promise<Company> =>
  new Promise((resolve, reject) => {
    new PropallyAgentV1Api().agentV1CompanyGet((error: object, data: Company, response: object) =>
      error ? reject(response ?? error) : resolve(data)
    );
  });

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();
  const setLoggedIn = useSetRecoilState(LoggedInState);
  const setAgent = useSetRecoilState(AgentState);
  const setCompany = useSetRecoilState(CompanyState);
  const resetAgent = useResetRecoilState(AgentState);
  const resetCompany = useResetRecoilState(CompanyState);
  const resetTags = useResetRecoilState(MasterAgentSearchTagsState);
  const resetTypes = useResetRecoilState(MasterInterestedPropertyTypesState);
  const resetTagStatus = useResetRecoilState(masterDataStatusState("agentSearchTags"));
  const resetTypeStatus = useResetRecoilState(masterDataStatusState("interestedPropertyTypes"));
  const resetUnread = useResetRecoilState(UnreadMatchingCount);
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const isPublicPage = ["/sign-in", "/sign-up", "/reset-password", "/new-password"].includes(pathname);
  const [authorization, setAuthorization] = useState<{ path: string; status: "checking" | "ready" | "error" }>({
    path: "",
    status: "checking",
  });
  const [attempt, setAttempt] = useState(0);
  // 永続化されたログイン状態と分け、このセッションで認証が確認できたかを保持する。
  const [hasAuthenticated, setHasAuthenticated] = useState(false);

  const clearSessionState = useCallback(() => {
    setHasAuthenticated(false);
    setLoggedIn({ logged_in: false });
    resetAgent();
    resetCompany();
    resetTags();
    resetTypes();
    resetTagStatus();
    resetTypeStatus();
    resetUnread();
    Sentry.setUser(null);
  }, [setLoggedIn, resetAgent, resetCompany, resetTags, resetTypes, resetTagStatus, resetTypeStatus, resetUnread]);

  const onAuthenticationError = useCallback(
    (error: unknown): boolean => {
      if (getApiErrorStatus(error) !== 401 && !isAccountAccessBlocked(error)) return false;
      clearSessionState();
      setAuthorization({ path: "", status: "checking" });
      enqueueSnackbar(
        isAccountAccessBlocked(error)
          ? "現在このアカウントは利用できません。管理者にお問い合わせください。"
          : "ログインの有効期限が切れました。もう一度ログインしてください。",
        { variant: "warning", preventDuplicate: true }
      );
      navigate("/sign-in", { replace: true });
      return true;
    },
    [clearSessionState, enqueueSnackbar, navigate]
  );

  useEffect(() => {
    if (!consumeImpersonationLoginFailed()) return;
    enqueueSnackbar("Adminログインに失敗したため、ブラウザに保存されているログイン情報で接続を試みます", {
      variant: "error",
    });
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (isPublicPage) {
      clearSessionState();
      setAuthorization({ path: "", status: "checking" });
      return;
    }
    let active = true;
    setAuthorization({ path: pathname, status: "checking" });
    // 通常/代理ログインともサーバーに確認する。sessionStorage の logged_in では取得を許可しない。
    Promise.all([fetchAgent(), fetchCompany()])
      .then(([agentData, companyData]) => {
        if (!active) return;
        setAgent({
          ...agentData,
          agent_profile_image: agentData.agent_profile_image?.image_url
            ? agentData.agent_profile_image
            : ({ image_url: "/user.svg" } as AgentProfileImage),
          // 紹介写真は任意。未登録（null や image_url 無し）は null に揃える
          agent_introduction_image: agentData.agent_introduction_image?.image_url
            ? agentData.agent_introduction_image
            : null,
        });
        setCompany(companyData);
        setLoggedIn({ logged_in: true });
        setHasAuthenticated(true);
        Sentry.setUser({ id: String(agentData.id) });
        setAuthorization({ path: pathname, status: "ready" });
      })
      .catch((error) => {
        if (!active || onAuthenticationError(error)) return;
        // 通信断/500/未知の403を認証切れとして扱わず、この画面から再確認できるようにする。
        setAuthorization({ path: pathname, status: "error" });
      });
    return () => {
      active = false;
    };
  }, [pathname, isPublicPage, attempt, clearSessionState, onAuthenticationError, setAgent, setCompany, setLoggedIn]);

  if (isPublicPage) return <>{children}</>;
  if (!hasAuthenticated && (authorization.path !== pathname || authorization.status === "checking")) {
    return <DoingOverlay open text="ログイン状態を確認しています" />;
  }
  const errorNotice = authorization.path === pathname && authorization.status === "error" && (
    <Box
      sx={
        hasAuthenticated
          ? { position: "fixed", bottom: 16, right: 16, maxWidth: "calc(100% - 32px)", zIndex: 1500 }
          : { maxWidth: 640, mx: "auto", mt: 8, px: 2 }
      }
    >
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            onClick={() => {
              setAuthorization({ path: pathname, status: "checking" });
              setAttempt((value) => value + 1);
            }}
          >
            再試行
          </Button>
        }
      >
        ログイン状態を確認できませんでした。通信状況を確認し、再試行してください。
      </Alert>
    </Box>
  );
  if (!hasAuthenticated) return <>{errorNotice}</>;
  // 一度確認できたセッションの再確認中・通信障害時はレイアウトを維持する。
  // 401 / 利用停止403のみ onAuthenticationError で認証状態とキャッシュを破棄する。
  return (
    <MasterDataProvider onAuthenticationError={onAuthenticationError}>
      {errorNotice}
      {children}
    </MasterDataProvider>
  );
};
